import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type EduRole = "student" | "teacher" | "school_admin";

interface EduState {
  loading: boolean;
  roles: EduRole[];
  schoolId: string | null;
  flags: Record<string, boolean>;
}

const EMPTY: EduState = { loading: true, roles: [], schoolId: null, flags: {} };
const cache = new Map<string, EduState>();

/**
 * Role + feature-flag state for the classroom features. Roles always come from
 * the database (education_roles), never from anything the browser supplies.
 */
export function useEducation() {
  const { user, isLoading } = useAuth();
  const key = user?.id ?? "anon";
  const [state, setState] = useState<EduState>(cache.get(key) ?? EMPTY);

  const load = useCallback(async () => {
    const flagRes = await supabase.from("feature_flags").select("key, enabled");
    const flags: Record<string, boolean> = {};
    for (const row of flagRes.data ?? []) flags[row.key] = row.enabled;

    if (!user) {
      const next = { loading: false, roles: [], schoolId: null, flags };
      cache.set("anon", next);
      setState(next);
      return;
    }
    const { data } = await supabase.rpc("edu_my_roles" as never, {} as never);
    const rows = (data as Array<{ role: EduRole; school_id: string | null }> | null) ?? [];
    const next: EduState = {
      loading: false,
      roles: rows.map((r) => r.role),
      schoolId: rows.find((r) => r.school_id)?.school_id ?? null,
      flags,
    };
    cache.set(user.id, next);
    setState(next);
  }, [user]);

  useEffect(() => {
    if (isLoading) return;
    const cached = cache.get(key);
    if (cached) setState(cached);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, isLoading]);

  const refresh = useCallback(async () => {
    cache.delete(key);
    await load();
  }, [key, load]);

  const chooseRole = useCallback(
    async (role: "student" | "teacher") => {
      const { error } = await supabase.rpc("edu_set_my_role" as never, { _role: role } as never);
      if (error) return error.message;
      await refresh();
      return null;
    },
    [refresh],
  );

  const isTeacher = state.roles.includes("teacher");
  const isSchoolAdmin = state.roles.includes("school_admin");
  const teacherPortalEnabled = state.flags["teacher_portal_enabled"] === true;
  const schoolPortalEnabled = state.flags["school_portal_enabled"] === true;

  return {
    ...state,
    isTeacher,
    isSchoolAdmin,
    // Every existing account is treated as a student unless told otherwise.
    isStudent: state.roles.includes("student") || (!isTeacher && !isSchoolAdmin),
    hasChosenRole: state.roles.length > 0,
    teacherPortalEnabled,
    schoolPortalEnabled,
    refresh,
    chooseRole,
  };
}
